'use client';

import { type ChangeEventHandler, forwardRef, useCallback, useMemo } from 'react';

import { cx } from '@/styled-system/css';

import { Select } from '../select/select';
import type { DataSelectPropsInterface } from './types/data-select.types';

const defaultDataTransform = (data: Array<any> | Record<any, any>) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === 'object') {
    return Object.keys(data).map((key) => ({ key, value: data[key] }));
  }

  throw new Error(`DataSelect: Cannot transform data of type: ${typeof data}`);
};

const defaultMapValueToProps: DataSelectPropsInterface['mapValueToProps'] = (val) => {
  let value = val;
  let key;
  let children;

  if (val && typeof val === 'object') {
    value = val.value;
    key = val.key;
    children = val.children;
  }

  key ??= value;
  children ??= key;

  return {
    value,
    key,
    children,
  };
};

/**
 * A DataSelect is a Select component whose `<option>`s are built from a data source.
 *
 * @since 5.0.0
 */
export const DataSelect = forwardRef<HTMLSelectElement, DataSelectPropsInterface>(
  (props: DataSelectPropsInterface, forwardedRef) => {
    const {
      data = [],
      dataTransform = defaultDataTransform,
      mapValueToProps = defaultMapValueToProps,
      onChange,
      onValueChange,
      ...rest
    } = props;

    const options = useMemo(() => {
      return dataTransform(data).map(mapValueToProps);
    }, [data, dataTransform, mapValueToProps]);

    const handleChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
      (e) => {
        onChange?.(e);

        if (!onValueChange) return;

        let value;
        let index = -1;

        for (const [i, option] of Object.entries(options)) {
          if (e.target.value === `${option.value}`) {
            value = option;
            index = Number(i);
            break;
          }
        }

        onValueChange(value, index);
      },
      [options, onChange, onValueChange],
    );

    return (
      <Select
        ref={forwardedRef}
        data-c11n-component="DataSelect"
        onChange={handleChange}
        {...rest}
        className={cx('c11n_data-select', rest.className)}
      >
        {options.map(({ key, ...optionProps }) => (
          <option key={key} {...optionProps} />
        ))}
      </Select>
    );
  },
);

DataSelect.displayName = 'DataSelect';
